/**
 * @file ContractDrawerTabsContainer.tsx
 * @description Owns the active-tab state for the drawer/mobile-page tab
 *   strip and renders the correct body for the active tab. The Live
 *   Tracking placeholder is a local concern of this container — it is
 *   mounted ONLY when the active tab is `live_tracking`, so the drawer
 *   body no longer always-renders the placeholder (which was the
 *   behavior before SE-4347 AC-6).
 * @module features/contracts/components/ContractDrawerTabs/ContractDrawerTabsContainer
 */

import React, { memo, useCallback, useState, type ReactNode } from "react";

import ClauseTrackingPlaceholder from "@/features/contracts/components/molecules/ClauseTrackingPlaceholder";

import ContractDrawerTabsView, {
  type ContractDrawerTabKey,
} from "./ContractDrawerTabsView";

import { Box } from "@/components/atoms/Box";

/** Props for {@link ContractDrawerTabsContainer}. */
export interface ContractDrawerTabsContainerProps {
  /**
   * When true, the Live Tracking tab is suppressed (archived rows show
   * only two tabs per the Lovable prototype + SE-4349 AC-4). Defaults
   * to `false`.
   */
  readonly isArchived?: boolean;
  /**
   * Tab to open with. Defaults to `"contract"`, the first tab in the
   * strip. Archived consumers that land on `"live_tracking"` will fall
   * back to `"contract"` because the Live Tracking tab is not rendered.
   * Ignored when `activeTab` is provided (controlled mode).
   */
  readonly initialTab?: ContractDrawerTabKey;
  /**
   * Controlled active tab. When provided, the container does NOT own
   * its own state — the parent is responsible for updating it via
   * `onTabChange`. When omitted, the container runs uncontrolled and
   * seeds from `initialTab`.
   */
  readonly activeTab?: ContractDrawerTabKey;
  /**
   * Called whenever the user presses a tab. Receives the tab key the
   * user asked for. In uncontrolled mode the container still updates
   * its internal state; in controlled mode the parent must apply the
   * change by passing the new `activeTab` prop back.
   */
  readonly onTabChange?: (tab: ContractDrawerTabKey) => void;
  /** Body content rendered under the Contract tab. */
  readonly contractSlot: ReactNode;
  /** Body content rendered under the Transfer Agreement tab. */
  readonly transferAgreementSlot: ReactNode;
}

/**
 * Guards the initial tab so an archived caller asking for
 * `live_tracking` (a hidden tab) does not end up with an orphaned
 * body. Falls back to the Contract tab.
 * @param initial - Caller-requested initial tab
 * @param isArchived - Whether the Live Tracking tab is hidden
 * @returns The tab to start on
 */
export function resolveInitialTab(
  initial: ContractDrawerTabKey | undefined,
  isArchived: boolean
): ContractDrawerTabKey {
  const requested = initial ?? "contract";
  if (requested === "live_tracking" && isArchived) return "contract";
  return requested;
}

/**
 * Container that owns tab state and mounts the active tab body.
 * @param props - Container props
 * @param props.isArchived - Hides Live Tracking when true
 * @param props.initialTab - Tab to open with; defaults to "contract"
 * @param props.activeTab - Controlled active tab; omit for uncontrolled mode
 * @param props.onTabChange - Fired whenever the user presses a tab
 * @param props.contractSlot - Body for the Contract tab
 * @param props.transferAgreementSlot - Body for the Transfer Agreement tab
 * @returns The tab strip with the active body mounted beneath it
 */
function ContractDrawerTabsContainer({
  isArchived = false,
  initialTab,
  activeTab: controlledActiveTab,
  onTabChange,
  contractSlot,
  transferAgreementSlot,
}: ContractDrawerTabsContainerProps) {
  const [internalActiveTab, setInternalActiveTab] =
    useState<ContractDrawerTabKey>(() =>
      resolveInitialTab(initialTab, isArchived)
    );
  const isControlled = controlledActiveTab !== undefined;
  const rawActiveTab = isControlled ? controlledActiveTab : internalActiveTab;
  // Re-resolve on every render so a mid-session flip of `isArchived`
  // (or a controlled caller passing "live_tracking" while archived)
  // can't leave the body blank — the tab strip reduces to two tabs in
  // archived mode and the active tab must stay one of them.
  const activeTab = resolveInitialTab(rawActiveTab, isArchived);

  const handleChangeTab = useCallback(
    (next: ContractDrawerTabKey) => {
      const resolvedNext = resolveInitialTab(next, isArchived);
      if (!isControlled) setInternalActiveTab(resolvedNext);
      onTabChange?.(resolvedNext);
    },
    [isArchived, isControlled, onTabChange]
  );

  const handlePressContract = useCallback(
    () => handleChangeTab("contract"),
    [handleChangeTab]
  );
  const handlePressTransferAgreement = useCallback(
    () => handleChangeTab("transfer_agreement"),
    [handleChangeTab]
  );
  const handlePressLiveTracking = useCallback(
    () => handleChangeTab("live_tracking"),
    [handleChangeTab]
  );

  return (
    <Box className="flex-1">
      <ContractDrawerTabsView
        isContractActive={activeTab === "contract"}
        isTransferActive={activeTab === "transfer_agreement"}
        isLiveActive={activeTab === "live_tracking"}
        isArchived={isArchived}
        onPressContract={handlePressContract}
        onPressTransferAgreement={handlePressTransferAgreement}
        onPressLiveTracking={handlePressLiveTracking}
      />
      {activeTab === "contract" ? contractSlot : null}
      {activeTab === "transfer_agreement" ? transferAgreementSlot : null}
      {activeTab === "live_tracking" && !isArchived ? (
        <ClauseTrackingPlaceholder />
      ) : null}
    </Box>
  );
}

ContractDrawerTabsContainer.displayName = "ContractDrawerTabsContainer";

export default memo(ContractDrawerTabsContainer);
